// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import qs from 'query-string';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { ChannelType } from '@/models/channel';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@heroui/react';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl, FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { type ModalProps } from '@/stores/use-modal-store.ts';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import { API_BASE } from '@/config';
import { useTranslation } from 'react-i18next';
import { useServerStore } from '@/stores/use-server-store.ts';
import { useSocket } from '@/context/socket-context/use-external-socket.ts';
import { TkitServer } from '@/models/tkit-server';

const formSchema = z.object({
  name: z
    .string()
    .min(1, {
      message: 'Channel name is required.',
    })
    .refine((name) => name !== 'general', {
      message: "Channel name cannot be 'general'",
    }),
  type: z.nativeEnum(ChannelType),
  isPrivate: z.boolean(),
  maxParticipants: z.number().min(1, { message: 'Must be at least 1 participant.' }),
  preview: z.boolean(),
  tkitServerId: z.string().optional(),
});

export const EditChannelModal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  data,
}) => {
  const { t } = useTranslation();
  const { channel } = data;
  const { server, updateChannels } = useServerStore();
  const socket = useSocket();
  const [tkitServers, setTkitServers] = useState<TkitServer[]>([]);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      type: channel?.type || ChannelType.TEXT,
      isPrivate: channel?.isPrivate || false,
      maxParticipants: channel?.maxParticipants,
      preview: channel?.preview ?? true,
      tkitServerId: channel?.tkitServerId ?? '',
    },
  });

  useEffect(() => {
    if (channel) {
      form.setValue('name', channel.name);
      form.setValue('type', channel.type);
      form.setValue('isPrivate', channel.isPrivate);
      form.setValue('maxParticipants', channel?.maxParticipants);
      form.setValue('preview', channel.preview);
      form.setValue('tkitServerId', channel?.tkitServerId || '');
    }
  }, [form, channel]);

  useEffect(() => {
    fetch(`${API_BASE}/api/tkit/servers`, {
      credentials: 'include',
    })
      .then((r) => r.json())
      .then(setTkitServers)
      .catch(console.error);
  }, []);

  const isLoading = form.formState.isSubmitting;

  const selectedType = form.watch('type');

  const supportsVoice =
    selectedType === ChannelType.AUDIO ||
    selectedType === ChannelType.VIDEO ||
    selectedType === ChannelType.STREAM ||
    selectedType === ChannelType.TRIBUNE;

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      const url = qs.stringifyUrl({
        url: `${API_BASE}/api/channels/${channel?.id}`,
        query: {
          serverId: server?.id,
        },
      });

      const response = await fetch(url, {
        method: 'PATCH',
        body: JSON.stringify(values),
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const updatedChannel = await response.json();

      updateChannels([
        ...(server?.channels || []).filter((c) => c.id !== updatedChannel.id),
        updatedChannel,
      ]);

      if (socket?.readyState === WebSocket.OPEN) {
        socket.send(
          JSON.stringify({
            type: 'channel-edit',
            payload: updatedChannel,
          })
        );
        // socket.send(
        //   JSON.stringify({
        //     type: 'channel-tkit-server-updated',
        //     updatedChannel.id,
        //     tkitServerId,
        //   })
        // );

        // Clients connected to that channel can:

        // disconnect();
        // fetchNewToken();
        // reconnect();
      }

      form.reset();
      onClose();
    } catch {
      // TODO: translate
      toast.error('Something went wrong. Cant edit channel.');
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={handleClose}>
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">{t('EditChannelModal.title')}</ModalHeader>
            <ModalBody>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                  <div className="space-y-2 px-6">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-bold uppercase">
                            {t('EditChannelModal.name')}
                          </FormLabel>
                          <FormControl>
                            <Input
                              disabled={isLoading}
                              placeholder={t('EditChannelModal.placeholder')}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="type"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('EditChannelModal.type')}</FormLabel>
                          <select
                            disabled={isLoading}
                            value={field.value}
                            onChange={field.onChange}
                            className="mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                          >
                            {Object.values(ChannelType).map((type) => (
                              <option key={type} value={type}>
                                {type.toLocaleUpperCase()}
                              </option>
                            ))}
                          </select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    {supportsVoice && (
                      <FormField
                        control={form.control}
                        name="tkitServerId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Локация</FormLabel>

                            {/*<FormDescription>*/}
                            {/*  Голосовой и видео трафик будет использовать данный узел*/}
                            {/*</FormDescription>*/}

                            <FormControl>
                              <select
                                disabled={isLoading}
                                value={field.value || ''}
                                onChange={field.onChange}
                                className="mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10"
                              >
                                {tkitServers.map((server) => (
                                  <option key={server.id} value={server.id}>
                                    {server.name || server.domain}
                                  </option>
                                ))}
                              </select>
                            </FormControl>

                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                    <FormField
                      control={form.control}
                      name="maxParticipants"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('EditChannelModal.maxParticipants')}</FormLabel>
                          <FormControl>
                            {/*TODO: change in API too max */}
                            <Input
                              type="number"
                              disabled={isLoading}
                              min={1}
                              max={50}
                              placeholder={t('EditChannelModal.maxParticipantsPlaceholder')}
                              {...field}
                              onChange={(e) => field.onChange(e.target.valueAsNumber)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    {/*<FormField*/}
                    {/*  control={form.control}*/}
                    {/*  name="preview"*/}
                    {/*  render={({ field }) => (*/}
                    {/*    <FormItem className="flex flex-row items-center justify-start gap-2">*/}
                    {/*      <FormLabel className="text-xs font-bold uppercase">*/}
                    {/*        {t('EditChannelModal.preview')}*/}
                    {/*      </FormLabel>*/}
                    {/*      <FormDescription>*/}
                    {/*        (experimental) media channels only*/}
                    {/*      </FormDescription>*/}
                    {/*      <FormControl>*/}
                    {/*        <Switch*/}
                    {/*          checked={field.value}*/}
                    {/*          onCheckedChange={field.onChange}*/}
                    {/*        />*/}
                    {/*      </FormControl>*/}
                    {/*      <FormMessage />*/}
                    {/*    </FormItem>*/}
                    {/*  )}*/}
                    {/*/>*/}
                    <FormField
                      control={form.control}
                      name="isPrivate"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-start gap-2">
                          <FormLabel className="text-xs font-bold uppercase">
                            {t('EditChannelModal.private')}
                          </FormLabel>
                          <FormControl>
                            <Switch checked={field.value} onCheckedChange={field.onChange} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <ModalFooter>
                    <Button color="primary" variant="primary" disabled={isLoading}>
                      {t('EditChannelModal.save')}
                    </Button>
                  </ModalFooter>
                </form>
              </Form>
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
